import { clamp, lerp, smoothstep } from "math";
import { useAnimationLoop, type UseAnimationLoop } from "../useAnimationLoop"

export type UseSmoothAnimationProps = {
  initial?: number;
}

export type UseSmoothAnimationCallOptions = {
  duration?: number;
  smoothness?: number;
}

export type UseSmoothAnimation = {
  current: ComputedRef<number>;
  loop: UseAnimationLoop;
  to: (target: number, options?: UseSmoothAnimationCallOptions) => Promise<void>;
  wait: () => Promise<void>;
}

export const useSmoothAnimation = (props: UseSmoothAnimationProps = {}): UseSmoothAnimation => {
  const current = ref<number>(props.initial ?? 0.0);
  const loop = useAnimationLoop({ cancelPreviousOnStart: true });


  const promises: Array<Promise<void>> = [];

  const to = (
    target: number,
    options: UseSmoothAnimationCallOptions = {}
  ) => {
    const promise = new Promise<void>((resolve) => {
      const val: number = current.value;
      const { duration = 1.0, smoothness = 1.0 } = options;

      loop.start((ctx) => {
        const scale = clamp(ctx.elapsed / duration, 0.0, 1.0);

        if (smoothness > 0.0) {
          current.value = lerp(val, target, lerp(scale, smoothstep(0.0, 1.0, scale), smoothness));
        } else {
          current.value = lerp(val, target, scale);
        }

        if (ctx.elapsed >= duration || scale >= 1.0) {
          ctx.stop();
          resolve();
        }
      });
    });
    promises.push(promise);
    return promise;
  }

  const output = computed(() => current.value);

  const wait = async () => {
    for (const p of promises) {
      await p;
    }
    promises.splice(0);
  }

  return {
    to,
    loop,
    current: output,
    wait
  }

}
